/**
 * Add and show views in particular contexts via actions - think of it as a view controller
 * Unlike the usual controller this is very privy to the DOM
 * @class
 */
import Base from './netx-view';
import EmptyTemplate from '@netx/core-app/templates/empty.html';

import AppViewsCollection from '@netx/core-app/collections/appViews';
import AppViewsQueueCollection from '@netx/core-app/collections/appViewsQueue';
import {
	RegisterLegacyAction,
	FindLegacyAction,
} from '@netx/core/legacyActionManager';

class MultiView extends Base {
	/** @type {boolean} */
	get isMultiView() {
		return this.constructor.__isMultiView__;
	}

	/**
	 * @override
	 * @listens {@link Netx.Collections.AppViews#change:showActions}
	 * @listens {@link Netx.Collections.AppViews#change:uniqueShowAction}
	 * @listens {@link Netx.Collections.AppViews#loaded}
	 * @listens {@link Netx.Collections.AppViews#loading}
	 * @listens {@link Netx.Collections.AppViews#swtichedBack}
	 * @listens {@link Netx.Collections.AppViews#view:hiding}
	 * @listens {@link Netx.Collections.AppViews#view:hidden}
	 * @listens {@link Netx.Collections.AppViews#view:removed}
	 * @listens {@link Netx.Collections.AppViews#view:rendered}
	 * @listens {@link Netx.Collections.AppViews#view:showing}
	 * @listens {@link Netx.Collections.AppViews#view:shown}
	 * @listens {@link Netx.Views.MultiView#addedNewChildView|addedChildView}
	 * @listens {@link Netx.Views.MultiView#removedChildView|removedChildView}
	 */
	constructor(opts = {}) {
		const viewCollection = opts.viewCollection || new AppViewsCollection([]);
		/**
		 * @property {object}  childView            - Child view object
		 * @property {string}  childView.viewClass  - The view class to use when creating `childView` (in our case, it is the `viewSpec` model's `id`)
		 * @property {string}  childView.target     - The css selector to use when injecting a newly created `childView` (to be overriden in extending `view`)
		 */
		opts.childView = {
			viewClass(model) {
				return model.get('viewClass');
			},
			target: false,
			collection: viewCollection,
			options: {
				attributes: {
					'data-layer': '',
				},
			},
			...opts.childView,
		};
		super(opts);

		this.childView = opts.childView;
		this.instantiateOnAdd = opts.instantiateOnAdd !== false;
		this.registeredViews = [];
		/**
		 * Classes that will be added to views when they enter our `viewCollection`
		 * These classes will be removed when they are removed from our `viewCollection`
		 * This allows for transient views to have what they need when thye move from `multiView` to `multiView`
		 * @type {string}
		 * @default
		 * @see {@link Netx.Views.MultiView#viewCollection|viewCollection}
		 */
		this.additionalViewClasses = opts.additionalViewClasses || '';

		/**
		 * Whether or not to confirm unsaved changes when `this` tries to hide
		 * @type {boolean}
		 * @default
		 */
		this.confirmUnsavedChanges = opts.confirmUnsavedChanges === true;

		/**
		 * When `this` hides, should we empty all children in our `viewCollection`
		 * @type {boolean}
		 * @default
		 * @see {@link Netx.Views.MultiView#viewCollection|viewCollection}
		 */
		this.emptyOnHide = opts.emptyOnHide === true;

		/**
		 * Should we hide `this` when our `viewCollection` reaches a length of 0
		 * @type {boolean}
		 * @default
		 * @see {@link Netx.Views.MultiView#viewCollection|viewCollection}
		 */
		this.hideOnEmpty = opts.hideOnEmpty === true;

		/**
		 * Should we hide `this` when a child view is removed
		 * @type {boolean}
		 * @default
		 * @see  {@link Netx.Views.MultiView#viewCollection|viewCollction}
		 */
		this.hideWhenChildViewRemoved = opts.hideWhenChildViewRemoved === true;

		/**
		 * Render view immediately after instantiate
		 * @type {boolean}
		 * @default
		 */
		this.renderOnInstatiate = opts.renderOnInstatiate === true;

		/**
		 * Should the `childView` be immediately shown when added to our `viewCollection`
		 * @type {boolean}
		 * @default
		 * @see {@link Netx.Views.MultiView#viewInstantiated|viewInstantiated}
		 * @see {@link Netx.Views.MultiView#viewCollection|viewCollection}
		 */
		this.showOnAdd = opts.showOnAdd !== false;

		// I am confident in doing this here as this cannot be populated until the first render of this view
		// and none of the data needs to be validated or passed to the server / shared amongst the rest of the app
		/**
		 * This is my collection of views.
		 * @memberOf Netx.Views.MultiView
		 * @alias viewCollection
		 * @type {Netx.Collections.AppViews}
		 */
		this.viewCollection = viewCollection;

		// For debug output - multiviews within multiviews
		let viewSpec = opts.viewSpec;

		this.listenTo(this.viewCollection, 'add', (viewSpec) => {
			this.registerView(viewSpec);

			if (!this.instantiateOnAdd) return;

			this.instantiateView(viewSpec);
		});

		this.listenTo(this.viewCollection, 'remove', (...args) => {
			this.removeChildView(...args);
		});

		this.listenTo(this.viewCollection, 'add remove reset', (collection) => {
			this.el.classList.toggle(
				'multiple-views',
				this.viewCollection.length > 1,
			);
		});

		////////////////////////////////
		// QUEUE
		////////////////////////////////
		/**
		 * In the event we need to prevent a view from showing for a time (until conditions are met)
		 * Or we receive a transient view that then leave (like a bookmark for the last view that was active before the transient came into our lives)
		 * @memberOf Netx.Views.MultiView
		 * @alias cachedActiveView
		 * @private
		 * @type {Netx.View}
		 */
		this.cachedActiveView = null;

		/**
		 * If we are in the middle of hiding - the best thing for all involved is to let it finish - show again and then pick up where the data left off.
		 * @memberOf Netx.Views.MultiView
		 * @alias _viewQueue
		 * @private
		 * @type {Netx.Collections.AppViews}
		 */
		this.viewCollection.viewQueue = this._viewQueue =
			new AppViewsQueueCollection([], {
				queueFor: this.viewCollection,
				view: this,
			});

		// NOTE: I am not adding `_viewQueueParentView` to `this.subViews` - as nothing in this view (other than remove)
		// should have any affect on it
		/**
		 * The reason for the dummy view is so that the queued views actually get created and can listen for any events that would affect them in the mean
		 * time while they are queued
		 * @memberOf Netx.Views.MultiView
		 * @alias _viewQueueParentView
		 * @private
		 * @type {Netx.View}
		 */
		this._viewQueueParentView = new Base({
			template: EmptyTemplate,
			collection: this._viewQueue,
		});
		// Make sure it reflects our `childView` object
		this._viewQueueParentView.childView = this.childView;

		// Hey, listen!
		this.listenTo(this, 'addedNewChildView', (view) => {
			// Defer so all actions have been registered in the viewSpec
			// These get set at the same time as this event
			_.defer(_.bind(_.partial(this.viewInstantiated, view), this));
		})
			.listenTo(this, 'removedChildView', (view) => {
				this.viewRemoved(view);
			})
			////////////////////////////////////
			// SHOW ACTION LISTENERS
			////////////////////////////////////
			.listenTo(
				this.viewCollection,
				'change:showActions',
				(model, collection) => {
					if (
						collection &&
						collection._listenId === this.viewCollection._listenId
					) {
						const prev = model._previousAttributes || {};
						const newActions = model.get('showActions') || [];
						const prevActions = prev.showActions || [];

						// Stop listening to actions no longer present
						_.each(_.difference(prevActions, newActions), (action) => {
							this.stopListening(dispatcher, action);
						});
						// Start lsitening to actions that were added
						_.each(_.difference(newActions, prevActions), (action) => {
							this.listenTo(
								dispatcher,
								action,
								_.partial(this.showView, model.get('view')),
							);
						});
					}
				},
			)
			.listenTo(
				this.viewCollection,
				'change:uniqueShowAction',
				(model, collection) => {
					if (
						collection &&
						collection._listenId === this.viewCollection._listenId
					) {
						var prev = model._previousAttributes || {},
							newAction = model.get('uniqueShowAction'),
							prevAction = prev.uniqueShowAction;

						// TODO: A way to remove actions after they have been defined - is it as simple as removing it from the Actions array?
						// if( prevAction.match( /^appView:(\w+):show/) ) {}

						// Register actions so we can run through the actions/event system
						// We assume that if you provided an action that does not match this pattern that it is a legitamte and registered action
						if (newAction && newAction.match(/^appView:(\w+):show/)) {
							RegisterLegacyAction([
								{
									event: newAction,
									label: i18n.t('i18n.show'),
								},
							]);
						}

						prevAction && this.stopListening(dispatcher, prevAction);
						newAction &&
							this.listenTo(
								dispatcher,
								newAction,
								_.partial(this.showView, model.get('view')),
							);
					}
				},
			)
			////////////////////////////////////
			// !SHOW ACTION LISTENERS
			////////////////////////////////////

			////////////////////////////////////
			// SWITCHING CONTEXTS
			////////////////////////////////////
			// A model has been switched to another `viewCollection`
			.listenTo(this.viewCollection, 'switched', (model, collection) => {
				if (model && model.isMyOriginalCollection(this.viewCollection)) {
					model.get('view') &&
						model.get('view').$el.removeClass(this.additionalViewClasses);
					model.set('switched', true);
					this.$el.addClass('view-switched');
				}
			})
			// A view was returned back to its original home
			.listenTo(this.viewCollection, 'switchedBack', (model, collection) => {
				// If our `viewCollection` is its original `viewCollection` - reactivate and show it
				if (
					collection &&
					this.viewCollection._listenId === collection._listenId
				) {
					// Ge the view
					const view = model.get('view');
					// Check if it shopuld be the active view
					const activeView = this.getActiveView();

					// We are no longer switched out
					model.set('switched', false);

					// This will get our element back into its right place in the DOM
					this.addChildView(model);
					// Put classes back
					view.$el.addClass(this.additionalViewClasses);
					// Either there are no active views or the active view is this view that is switching back
					if (_.isUndefined(activeView) || activeView.cid === view.cid) {
						// Reactivate
						model.set('active', true);
						// Show
						this.showView(view);
					}
					// We are just put back but not active
					else {
						// Deactivate
						model.set('active', false);
					}
					// Toggle class based on if any views are still switched out from our `viewCollection`
					this.$el.toggleClass(
						'view-switched',
						!_.isUndefined(this.viewCollection.findWhere({switched: true})),
					);
				}
			})
			////////////////////////////////////
			// !SWITCHING CONTEXTS
			////////////////////////////////////

			// If it is a view event the first argument will always be the view that the event was triggered on
			// due to the fact that we are not consistent in always providing a the view when we trigger an event
			// Collection/model events will be piped through as-is
			// View events are prepended with "view:" so we do not conflict with data events
			// this.listenTo( this.viewCollection, 'all', function() {
			// 	console.log( arguments );
			// } );

			// Sometimes a view is removed by other means than being taken out of the viewCollection
			// Be safe and remove from the collection as well (if it does not exist there is no harm)
			// It will be caught in the 'removedChildView' listener and dealt with
			.listenTo(this.viewCollection, 'view:removed', (view) => {
				this.viewCollection.remove(view.viewSpec);
			})
			.listenTo(this.viewCollection, 'view:hiding', this.viewOnHiding)
			.listenTo(this.viewCollection, 'view:hidden', (view) => {
				this.viewAfterHidden(view);
			})
			.listenTo(this.viewCollection, 'view:showing', this.viewOnShowing)
			.listenTo(this.viewCollection, 'view:shown', (view) => {
				this.viewAfterShown(view);
			});

		// When a child view is hidden we hide the multiView
		if (this.hideWhenChildViewRemoved) {
			// When any views hide we all hide
			this.listenTo(this.viewCollection, 'remove', (model) => {
				this.hide();
			});
		}
	}

	/** @override */
	getExcludedViews() {
		const excludedViews = super.getExcludedViews();
		return excludedViews.concat(_.pluck(this.childViews, 'el'));
	}

	/**
	 * @method
	 */
	registerView(viewSpec) {
		if (this.registeredViews.indexOf(viewSpec.id) > -1) return;
		this.registeredViews.push(viewSpec.id);
		// Listen for each show:action
		viewSpec.get('showActions').forEach((action) => {
			this.listenTo(dispatcher, action, () => {
				let view = viewSpec.get('view');
				if (!view) {
					view = this.instantiateView(viewSpec);
				}
				this.showView(view);
			});
		});

		this.listenTo(dispatcher, viewSpec.get('uniqueShowAction'), () => {
			let view = viewSpec.get('view');
			if (!view) {
				view = this.instantiateView(viewSpec);
			}
			this.showView(view);
		});
	}

	/**
	 * @method
	 */
	instantiateView(viewSpec) {
		if (!this.$childTargetEl || viewSpec.get('view')) return;

		const viewClass = viewSpec.get('viewClass');
		let opts = this.childView.options;
		if (typeof opts === 'function') {
			opts = opts.call(this, viewSpec);
		} else {
			_.each(opts, (opt, key, all) => {
				if (
					key === 'template' ||
					key === 'onConfirmed' ||
					key === 'onCancelled'
				)
					return;
				if (
					// Do not execute method overrides - leave them as is
					_.isFunction(opt) &&
					!opt.__isView__
				) {
					all[key] = opt.call(this, viewSpec);
				}
			});
		}

		opts.viewSpec = viewSpec;
		let viewOptions = viewSpec.get('viewOptions') || {};
		if (typeof viewOptions === 'function') {
			viewOptions = viewOptions.call(this, viewSpec);
		} else {
			_.each(viewOptions, (opt, key, all) => {
				if (
					key === 'template' ||
					key === 'onConfirmed' ||
					key === 'onCancelled'
				)
					return;
				if (
					// Do not execute method overrides - leave them as is
					_.isFunction(opt) &&
					!opt.__isView__
				) {
					all[key] = opt.call(this, viewSpec);
				}
			});
		}

		const view = new viewClass({...opts, ...viewOptions});
		viewSpec.set('view', view);
		this.childViews = this.childViews || {};
		this.childViews[viewSpec.cid] = view;
		this.trigger('addedNewChildView', view);

		if (!this.$childTargetEl) return view;

		this.$childTargetEl.append(view.$el);
		this.renderOnInstatiate && view.render();

		return view;
	}

	/**
	 * @override
	 * @param {boolean} simple Simple hide (not up for removal - just visibility change)
	 */
	hide(simple) {
		// If it is not a simple hide and we want to confirm unsaved changes and we have not previously confirmed unsaved changes
		if (
			simple === true ||
			!this.confirmUnsavedChanges ||
			this.confirmedUnsavedChanges
		) {
			return super.hide(simple);
		}

		const viewCollection = this.viewCollection;

		// Loop over our child views and check for a hide confirmations (unsaved changes)
		// We will only do the first one we find since wedo not have a great way yet to present a user with all the info about each unsaved view
		// So, if there are 2+ unsaved views, we confirm for one and it confirms for all
		let unsavedView;
		for (let i = 0, c = viewCollection.length, view; i < c; i++) {
			view = viewCollection.at(i).get('view');
			if (view && !view.confirmedUnsavedChanges) {
				unsavedView =
					_.isFunction(view.unsavedChanges) && view.unsavedChanges();
				if (unsavedView) break;
			}
		}

		// When all is ready
		return this.when(unsavedView ? unsavedView.confirmHide() : true).then(
			(view) => {
				// We only confirm once - all other child views will be considered confirmed as well
				this.viewCollection.each((viewSpec) => {
					const v = viewSpec.get('view');
					if (!v) return;
					v.confirmedUnsavedChanges = true;
				});
				// We are good to hide
				this.resetActiveView();
				super.hide(simple);
			},
			(view) => {
				// User opted not to hide, show the view
				this.showView(view);
			},
		);
	}

	/**
	 * Everything has happend in the core hiding of `this`
	 * Executes just before `whenHidden` is resolved
	 * @override
	 * @listens {@link Netx.View#hidden}
	 * @see {@link Netx.View#whenHidden|whenHidden}
	 */
	afterHidden(view) {
		super.afterHidden(view);

		if (!this.emptyOnHide) return;

		// We defer so that anything running on hidden can finish
		_.defer(() => {
			// Views that wanted to show while we were hiding momentarily
			if (!this._viewQueue.length) return;
			// Apply queue
			this.viewCollection.set(this._viewQueue.models);
			// Empty
			this._viewQueue.reset([]);
		});
	}

	/**
	 * We will need to cancel out any remaining views here as we can get collisions on after hidden
	 * @method
	 */
	beforeHidden(cb) {
		super.beforeHidden(cb);

		if (!this.emptyOnHide) return this;

		this.viewCollection.each((viewSpec) => {
			const view = viewSpec.get('view');
			view && _.isFunction(view.cancel) && view.cancel();
		});
		// Always use set to empty
		this.viewCollection.set([]);
	}

	/** @override */
	checkHasResults() {
		// We may have a collection but it is not data related nor should it
		// be setting any no-results/no-assets attributes
		// Will need to deal with when we refactor the loading class stuff
		return true;
	}

	/** @override */
	postRender() {
		super.postRender();

		const viewCollection = this.viewCollection;

		// Cache
		this.$childTargetEl = this.$(this.childView.target || '.nx-childviews');
		if (!this.$childTargetEl.length) {
			this.$childTargetEl = this.$el;
		}

		if (
			!this.getActiveView() &&
			this.instantiateOnAdd &&
			viewCollection.length
		) {
			this.instantiateView(viewCollection.at(0));
		}

		this.el.classList.toggle('multiple-views', viewCollection.length > 1);
	}

	/** @override */
	remove() {
		this.lastViewSpec = this.currentViewSpec = null;
		// Clear queue
		this._viewQueueParentView.remove();
		this._viewQueue.reset([]);
		this._viewQueue.garbageCollect();

		_.invoke(this.childViews, 'remove');

		super.remove();
	}

	/**
	 * Respond to `collection` remove events by removing the associated `childView`, if it exists.
	 * Only respond if the app view has no where else to go - otherwise leave the view and its events intact
	 * @override
	 * @see {@link Netx.Views.MultiView#viewCollection|viewCollection}
	 */
	removeChildView(model, collection, opts = {}) {
		// Make sure it was removed from our `collection`
		if (!this.childViews || collection !== this.viewCollection) return;

		const childView = model.get('view');
		if (!childView) return;

		// Leave the events and elements intact
		if (!model.collections.length) {
			childView.remove();
		}
		// This only pertains to this view
		delete this.childViews[model.cid];
		this.stopListening(childView);
		this.trigger('removedChildView', childView);
	}

	/**
	 * Do any of our children have unsaved changes?
	 * @override
	 * @return {boolean|Netx.View} false / first unsaved instance of Netx.View
	 */
	unsavedChanges() {
		const viewCollection = this.viewCollection;

		// Loop over our `viewCollection` and find the first `childView` with unsaved changes
		let hasUnsavedChanges = false;
		for (let i = 0, c = viewCollection.length, view; i < c; i++) {
			view = viewCollection.at(i).get('view');
			if (view && view.unsavedChanges()) {
				hasUnsavedChanges = view;
				break;
			}
		}

		return hasUnsavedChanges;
	}

	/**
	 * Get the active `childView`
	 * @method
	 * @return {Netx.View} Active `childView` instance or `undefined`
	 */
	getActiveView() {
		const viewSpec = this.viewCollection.findWhere({active: true});
		return viewSpec ? viewSpec.get('view') : undefined;
	}

	/**
	 * Find the first enabled (via `showActions`/`uniqueShowAction`) `childView` and show it.
	 * Pass a `viewId` if you want to diqualify a particular `childView` from being considered.
	 * @method
	 * @param {string} viewId If provided it will get the first `view` not to match `viewId`
	 * @return {boolean|Netx.Models.AppView} First enabled instance of {@link Netx.Models.AppView} or `false`
	 */
	getFirstEnabledView(viewId) {
		const activeView = this.getActiveView();
		const viewCollection = this.viewCollection;
		const collectionLength = viewCollection.length;

		if (
			collectionLength < 2 ||
			(activeView &&
				activeView.viewSpec.id !== viewId &&
				activeView.viewSpec.get('name') !== viewId)
		)
			return false;

		// Either there is no `activeView` or the `activeView` is the view we do not want and we have enough views to do anything
		// Look for the first view that is not the one we could not show and is also valid to show
		for (let i = 0, viewSpec; i < collectionLength; i++) {
			viewSpec = viewCollection.at(i);
			if (
				(!viewId ||
					(viewSpec.id !== viewId && viewSpec.get('name') !== viewId)) &&
				this.isViewActionValid(viewSpec)
			) {
				return viewSpec;
			}
		}
		return false;
	}

	/**
	 * Get a `childView` by `name`
	 * @method
	 * @param {string} name View name
	 * @return {Netx.View} `Netx.View` instance or `undefined`
	 */
	getViewByName(name) {
		const viewSpec =
			this.viewCollection.findWhere({name: name}) ||
			this.viewCollection.findWhere({viewClass: name});
		if (!viewSpec) return;

		return viewSpec.get('view');
	}

	/**
	 * Hide every `childView`
	 * @method
	 */
	hideAllViews() {
		_.invoke(this.childViews, 'hide', true);
		return this;
	}

	/**
	 * Check the unqiue show `action` of a `viewSpec`
	 * @method
	 * @param {Netx.Models.AppView} viewSpec View spec
	 * @return {boolean} View `action` valid
	 * TODO: Refactor actions and break up the pieces, like tests and synced listeners etc
	 */
	isViewActionValid(viewSpec) {
		if (!viewSpec) return false;
		return viewSpec.isViewActionValid();
	}

	/** @method */
	loadedView() {
		this._loadingView = false;
		this.reflectLoadingTimeout && clearTimeout(this.reflectLoadingTimeout);
		this.el.removeAttribute('data-nx-loading-view');
	}

	/** @method */
	loadingView() {
		if (this._loadingView) return;

		this._loadingView = true;
		this.reflectLoadingTimeout && clearTimeout(this.reflectLoadingTimeout);
		this.reflectLoadingTimeout = setTimeout(() => {
			if (!this._loadingView) return;
			this.el.setAttribute('data-nx-loading-view', 'true');
		}, 48);
	}

	/**
	 * Set the active `childView` to inactive
	 * @method
	 */
	resetActiveView() {
		const viewSpec = this.viewCollection.findWhere({active: true});
		viewSpec && viewSpec.set('active', false);
		// Chain
		return this;
	}

	/**
	 * Set a class on `this.$el` to reflect the currently active `view`
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 */
	setClassName(view) {
		// Set class this.$el
		this.$el
			.removeClass((index, css) => {
				// We want to remove all classes matching '*-layer'
				return (css.match(/\S+-layer/g) || []).join(' ');
			})
			.addClass(
				`${view.viewSpec.get('name') || _.toCamelCase(view.viewSpec.id)}-layer`,
			);
		// Chain
		return this;
	}

	/**
	 * Show a `childView`
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 */
	showView(view) {
		if (!this.viewChanging(view)) return this;

		// Show `this`
		this.show();

		// Clean up any exisiting promise
		if (this.viewReady && this.viewReady.state() === 'pending') {
			this.viewReady.rejectWith(this);
		}
		this.viewReady = new $.Deferred();

		this.loadingView();

		this.lastViewSpec = this.currentViewSpec;
		this.currentViewSpec = view.viewSpec;

		// Let `this` view respond to the fact we are about to call `show` on `view`
		this.beforeShowView(view).then(() => {
			this.whenShown.then(() => {
				// Delay hide
				// All views will live for at least 500ms - otherwise I have no idea  what the interface is trying to do.
				const d = new $.Deferred();
				view.hideDelayed = d.promise();

				_.delay(_.partial(d.resolveWith, this), view.minimumShowTime || 250);

				// Then call `show` on `view`
				view.show();

				this.loadedView();
			});
		});

		return this;
	}

	/**
	 * Show will be starting soon
	 * At this point not even `view.show` has been executed
	 * This allows the view to do anything special it needs to do in anticipation of calling `view.show`
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 * @return {Promise} Promise
	 */
	beforeShowView(view) {
		// Multi-view does not currently have any default functionality here
		// But this mehtod is only implemented in multi-view - other views extending this should call `super`
		// unless certain that they will always override any behavior that may be later placed here

		// FUN FACT: You cannot even put the word in a comment (without spaces) `_ s u p e r` - backbone super
		// must parse the functions as a string and search for an instance of that word (regardless of context)
		// and if it finds it, it tries to wrap the method - if the method's super does not implement the function
		// you get `Uncaught Super does not implement this method: <methodName>`

		// This method is for letting the view know a `childView` is going to be shown (before `show` is ever called)
		// We wait for our view to be shown before showing any `childViews` - but we may want to do something
		// in anticipation for the showing of a `childView` prior to any delays

		// Now we support promises (useful for when what we are doing may involve deferred modules)
		// All methods overriding this must return super or return and resolve `this.viewReady`
		const deferredModules = view.viewSpec.get('deferredModules');
		if (!_.isFunction(deferredModules)) {
			return this.viewReady.resolveWith(this);
		}

		deferredModules(() => {
			this.viewReady.resolveWith(this);
		});
		return this.viewReady.promise();
	}

	/**
	 * Find and the show the first enabled (via `showActions`/`uniqueShowAction`) `childView` and show it.
	 * Pass a `viewId` if you want to diqualify a particular `childView` from being considered.
	 * @method
	 * @param {number} viewId If provided it will show the first `view` not to match `viewId`
	 * @return {boolean|Netx.Models.AppView} First enabled instance of {@link Netx.Models.AppView} or `false`
	 */
	showFirstEnabledView(viewId) {
		const viewSpec = this.getFirstEnabledView(viewId);
		if (!viewSpec) return;

		const action = FindLegacyAction(viewSpec.get('uniqueShowAction'));
		action && this.performAction(action, [viewSpec.get('view'), null]);

		return viewSpec;
	}

	/**
	 * Show a `childView` by `name`
	 * @method
	 * @param {string} name `childView` name
	 */
	showViewByName(name) {
		let view = this.getViewByName(name);

		if (!view) {
			let viewSpec = this.viewCollection.findWhere({name: name});
			view = viewSpec && this.instantiateView(viewSpec);
		}

		view && this.showView(view);

		// Chain
		return this;
	}

	/**
	 * Provided with the view we are changing to and the knowledge of the current view
	 * you can block the change by returning `false`
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 * @returns {boolean} Ok to change/show view
	 */
	viewChanging(view) {
		// Transient views:
		// It should be noted - the view will go away still if you change views in its original `viewCollection` - since you are
		// affecting its original home - it will return (like in the category edit view - clicking another tab in the sidebar -
		// since that is where the category edit view belongs).
		// I think this is the desired behavior and avoids a lot of complications we would have if we tried to manage any deeper
		// than this. If we do, for some reason, decide we want to block it on all fronts - we will need to add this logic check
		// to the removal method in `multi-view` and check if the view being removed is `switched` and if so if it is blocking and
		// leave it be - but then you are entering a world of headaches I would imagine... it would be really hard to not create
		// collisions eventually. I think we would also need to add an idea of a queued active view when an official switch back
		// does finally occur.
		// If we have an active view and that view did not orginalyy belong to us, check its `transientOptions`
		const activeView = this.getActiveView();
		if (
			activeView &&
			!activeView.viewSpec.isMyOriginalCollection(this.viewCollection)
		) {
			const transientOpts = activeView.transientOptions || {};
			if (transientOpts.blocking) {
				// Update `cachedActiveView` (so we will go to the view that was trying to show when done with this blocking one)
				this.cachedActiveView = view;
				return false;
			}
		}
		return true;
	}

	/**
	 * Everything has happend in the core hiding of this `childView`
	 * Executes just before `whenHidden` is resolved
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 * @listens {@link Netx.Views.MultiView#view:hidden|view:hidden}
	 * @see {@link Netx.View#whenHidden|whenHidden}
	 */
	viewAfterHidden(view) {
		// Multi-view does not currently have any default functionality here
		// But this mehtod is only implemented in multi-view - other views extending this should call `super`
		// unless certain that they will always override any behavior that may be later placed here
	}

	/**
	 * Everything has happend in the core showing of this `childView`
	 * Executes just before `whenShown` is resolved
	 * @method
	 * @param {Netx.View} view Instance of {@link Netx.View} (created via {@link Netx.Models.AppView})
	 * @listens {@link Netx.Views.MultiView#view:shown|view:shown}
	 * @see {@link Netx.View#whenShown|whenShown}
	 */
	viewAfterShown(view) {
		this.setClassName(view);

		// TODO: if the following view is one that renders when its collection fetches
		// and that is happening or is currently happening - we will get a double render
		// But I can't know if the view is going to need rendering or not...
		// We could check ( view.collection || view.model ) `syncedOnce` || `syncing` but
		// we still do not know if the view is listening to that. Until we figure out a
		// good pattern - the double render will occur - it is better than the view not
		// rendering at all by chance
		!view.renderedOnce && view.render();
	}

	/**
	 * A `childView` was instantiated (a brand new `childView` was born - or at least as far as this view is concerned)
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 * @listens {@link Netx.Views.MultiView#childViewAdded|childViewAdded}
	 */
	viewInstantiated(view) {
		const viewSpec = view.viewSpec;

		// So we can out the right one back when our visitor leaves (if nothing else steps up to the plate)
		if (!viewSpec.isMyOriginalCollection(this.viewCollection)) {
			this.cachedActiveView = this.getActiveView();
		}

		// We are only enabled on instatiate if valid
		viewSpec.set('enabled', this.isViewActionValid(viewSpec));

		////////////////////////////////
		// LISTENERS
		////////////////////////////////
		const action = FindLegacyAction(viewSpec.get('uniqueShowAction')) || {};
		if (action.testOnSync) {
			view.collection &&
				this.listenTo(
					view.collection,
					'sync reset',
					_.partial(this.viewActionSynced, view),
				);
			view.model &&
				this.listenTo(
					view.model,
					'sync',
					_.partial(this.viewActionSynced, view),
				);
		}
		////////////////////////////////
		// !LISTENERS
		////////////////////////////////

		// Rather than doing this on the `childView` object we must do it manually
		// otherwise we need to start modifying the `className` or we get the classes back on each render
		view.$el.addClass(this.additionalViewClasses);

		// Reset confirmation
		view.confirmedUnsavedChanges = false;

		// This is the first time - so show it now
		// Or we were added from an existing view collection and were active
		if (this.showOnAdd || view.viewSpec.get('active')) {
			this.showView(view);
		}
	}

	/**
	 * A `model` or `collection` related to a `childView`'s `showAction`/`uniqueShowAction` has syced, check if we should be enabled or not
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 * TODO: Refactor actions and break up the pieces, like tests and synced listeners etc
	 */
	viewActionSynced(view) {
		const viewSpec = view && view.viewSpec;
		viewSpec && viewSpec.set('enabled', this.isViewActionValid(viewSpec));
	}

	/**
	 * `hide` is about to start on a `childView`
	 * At this point no animations or actual hiding [should] have taken place
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 * @listens {@link Netx.Views.MultiView#view:hiding|view:hiding}
	 */
	viewOnHiding(view) {
		// Only change its active status to `false` if it beloings to our `viewCollection`
		view.viewSpec.isMyOriginalCollection(this.viewCollection) &&
			view.viewSpec.set('active', false);
		// A defer here - just as with `viewRemoved` - there can always be that perfect timing where a new view is coming in
		// A defer is sufficient to avoid these perfect timing bad deals
		_.defer(() => {
			// We are not already hiding, the view will be removed on hide, this is the only view left and we want to hide when empty
			if (
				!this.hiding &&
				view.removeOnHide &&
				this.hideOnEmpty &&
				this.viewCollection.length === 1 &&
				(!view.viewSpec || this.viewCollection.at(0).id === view.viewSpec.id)
			) {
				this.hide();
			}
		});
	}

	/**
	 * `show` is about to start on a `childView`
	 * At this point no animations or actual hiding [should] have taken place
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 * @listens {@link Netx.Views.MultiView#view:showing|view:showing}
	 */
	viewOnShowing(view) {
		// DAM-18927 - don't activate if another view has taken it's place
		if (view.viewSpec.cid === this.currentViewSpec.cid) {
			// Set as active view
			view.viewSpec.set('active', true);
		}
	}

	/**
	 * A `childView` was removed
	 * @method
	 * @param {Netx.View} view View (created via {@link Netx.Models.AppView})
	 * @listens {@link Netx.Views.MultiView#removedChildView|removedChildView}
	 */
	viewRemoved(view) {
		// Cleanup classes that were added
		view.$el.removeClass(this.additionalViewClasses);

		// If we were the cached view - we are not anymore
		if (this.cachedActiveView && view.cid === this.cachedActiveView.cid) {
			delete this.cachedActiveView;
		}

		const idx = this.registeredViews.indexOf(view.viewSpec.id);
		idx > -1 && this.registeredViews.splice(idx, 1);

		////////////////////////////////
		// STOP LISTENERS
		////////////////////////////////
		// See `viewInstantiated` method
		this.stopListening(view);
		_.each(view.viewSpec.get('showActions'), (action) => {
			this.stopListening(dispatcher, action);
		});
		this.stopListening(dispatcher, view.viewSpec.get('uniqueShowAction'));
		view.collection && this.stopListening(view.collection, 'sync reset');
		view.model && this.stopListening(view.model, 'sync');
		////////////////////////////////
		// !STOP LISTENERS
		////////////////////////////////

		// This should have happend in the viewHidden method - but just in case it was bypassed
		// This only happens if our `viewCollection` is the `viewSpec`'s original collection
		view.viewSpec.isMyOriginalCollection(this.viewCollection) &&
			view.viewSpec.set('active', false);

		// Vars
		const activeView = this.getActiveView();
		const activeViewMatch =
			!activeView || activeView.viewSpec.id === view.viewSpec.id;

		// Make sure we are not already hiding or removing
		if (this.hiding || this.removing) return;

		// Wait for the chance a new view may have been added
		_.defer(() => {
			if (this.removing) return;
			// There is nothing left to show - hide this
			if (!this.viewCollection.length) {
				this.hideOnEmpty && this.hide();
			} else {
				let viewToShow;
				if (view.viewSpec !== this.currentViewSpec) {
					// A new current view has already been set in this time but is not visible
					const currentView = this.currentViewSpec.get('view');
					if (!currentView.isShown) {
						viewToShow = currentView;
					}
				}
				// Either there is no defined active view or the view we just removed was the active one and no others are active
				else if (activeViewMatch && !this.getActiveView()) {
					if (this.lastViewSpec && this.viewCollection.get(this.lastViewSpec)) {
						viewToShow = this.lastViewSpec.get('view');
					} else {
						const keys = _.keys(this.childViews);
						viewToShow =
							this.cachedActiveView || this.childViews[keys[keys.length - 1]];
					}
				}
				// We should not be in a situation where we want to show something that is already hiding/removing
				if (
					viewToShow &&
					!viewToShow.removing &&
					(!viewToShow.hiding || !viewToShow.removeOnHide)
				) {
					this.showView(viewToShow);
				}
			}
			delete this.cachedActiveView;
		});
	}

	/** @type {boolean} */
	static get __isMultiView__() {
		return true;
	}

	/** @type {string} */
	static get __name__() {
		return 'MultiView';
	}
}

export default MultiView;
